import Link from 'next/link';
import { MouseEventHandler, useRef, useState } from 'react';

import Logo from 'public/icons/Foco_Logo_Site.svg';
import styles from 'styles/Layout/Navbar.module.css';

import { MenuType } from '@/types';

import { useSession } from '@/contexts/session';
import { useRouter } from 'next/router';
import { useSnapshot } from 'valtio';
import useOnClickOutside from '@/hooks/useOnClickOutside';

import { portalClienteCreateAccount, portalClienteUrl } from '@/helpers/portal-cliente';
import IconToggleDropdown from './icons/IconToggleDropdown';
import IconUserDesktop from './icons/IconUserDesktop';
import IconUserMobile from './icons/IconUserMobile';
import { IconCashback } from './icons/IconCashback';

import reservation_state, { ReservationContextInterface } from '@/contexts/reservation_state';

import DropdownContractIcon from 'public/icons/contract.svg';
import DropdownHistoryIcon from 'public/icons/dropdown-history.svg';
import DropdownUserIcon from 'public/icons/dropdown-user.svg';
import DropdownVehicleIcon from 'public/icons/dropdown-vehicle.svg';

type NavbarProps = {
  isOpen: boolean;
  close: any;
  toggle: MouseEventHandler<HTMLDivElement>;
  menus: MenuType[];
  setMenus: (m: MenuType[]) => void;
};

export default function Navbar({
  isOpen,
  close,
  toggle,
  menus = [],
  setMenus,
}: NavbarProps) {
  const router = useRouter();
  const { customer, isFetching, logout } = useSession();
  const estadoSnapshot = useSnapshot(reservation_state);

  const ref = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useOnClickOutside(ref, () => {
    setIsDropdownOpen(false);
  });

  const handleCreateAccountClick = () => {
    router.push(portalClienteCreateAccount(router.query, { returnPath: router.pathname, reservationContext: estadoSnapshot as ReservationContextInterface }));
  }

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  }

  const handleLogoutClick = () => {
    setIsDropdownOpen(false);
    logout();
  }

  return (
    <div className={styles.navbar} onClick={close}>
      <div className={styles.navbar__wrap}>
        <div className={styles.logo__wrap}>
          <Link href="https://aluguefoco.com.br" legacyBehavior>
            <a>
              <Logo />
            </a>
          </Link>
        </div>

        {/* Usuário Logado */}
        {customer && (
          <div className={styles.user__wrap} ref={ref}>

            {/* Trigger */}
            <div className={styles.user__info} onClick={toggleDropdown}>
              <div className={styles.user__info__wrap}>
                <IconUserMobile className={styles.user__icon_mobile} />
                <IconUserDesktop className={styles.user__icon_desktop} />
                <span className={styles.user__name}>{customer.name}</span>
              </div>

              <IconToggleDropdown className={styles.icon_toggle_dropdown} />
            </div>

            {/* Dropdown */}
            {isDropdownOpen && (
              <div className={`${styles.dropdown} ${isDropdownOpen ? styles.dropdown_open : styles.dropdown_close}`}>
                <div className={styles.dropdown__wrap}>
                  <Link className={styles.dropdown__item} href={portalClienteUrl()}>
                    <DropdownUserIcon className={styles.dropdown__icon} />
                    Meu Cadastro
                  </Link>
                  <Link className={styles.dropdown__item} href={portalClienteUrl('/reservas-ativas')}>
                    <DropdownVehicleIcon className={styles.dropdown__icon} />
                    Reservas Ativas
                  </Link>
                  <Link className={styles.dropdown__item} href={portalClienteUrl('/contratos')}>
                    <DropdownContractIcon className={styles.dropdown__icon} />
                    Contratos
                  </Link>
                  <Link className={styles.dropdown__item} href={portalClienteUrl('/historico')}>
                    <DropdownHistoryIcon className={styles.dropdown__icon} />
                    Histórico
                  </Link>
                  <Link className={styles.dropdown__item} href={portalClienteUrl('/cashback')}>
                    <IconCashback className={styles.dropdown__icon} />
                    Cashback Volta Certa
                  </Link>
                  <button type="button" className={`${styles.dropdown__item} ${styles.dropdown__item_button}`} onClick={handleLogoutClick} disabled={isFetching}>
                    Sair
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Usuário deslogado */}
        {!customer && (
          <div className={styles.login__wrap}>
            <Link className={styles.login__wrap__href} href={`/login?${new URLSearchParams(router.query as Record<string, string>).toString()}&ref=${router.pathname}`}>
              <IconUserMobile className={styles.login__wrap__icon_mobile} />
              <IconUserDesktop className={styles.login__wrap__icon_desktop} />
              <span className={styles.login__wrap__signin}>Entrar</span>
            </Link>
            <p className={styles.login__wrap__divider}>|</p>
            <button type="button" className={styles.login__wrap__signup} onClick={handleCreateAccountClick}>Criar Conta</button>
          </div>
        )}
      </div>
    </div>
  );
}
